import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import styled from "styled-components";
import {
    AppRemixIcon,
    AppInput,
    AppMultiSelect,
    AppSingleSelect,
    AppDatePicker,
    AppModal,
} from "@/layout/AppComponents";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";
import ReactDOMServer from "react-dom/server";
import _ from 'lodash';
import * as currentPageAction from "../../../../store/actions/currentPageAction";
import Translator from "../../../components/helpers/Translator";
import { axiosPath, path } from "../../../../routing/helpers/router";
import { getDocumentManagerFilterInitialValues } from "../../../../minions/forms";
import useIsMounted from "../../../components/hooks/useIsMounted";
import LocationSelect from "../../../components/form/Selects/LocationSelect";
import colors from "../../../../constants/colors";

export default function FilterBlock() {
    const { moduleBridgeHex, parentItemHex, filtersHex } = useParams();
    const dispatch = useDispatch();
    const isMounted = useIsMounted();
    const navigate = useNavigate();
    const filters = useSelector((state) => state.currentPage?.filters);
    const moduleData = useSelector(
        (s) => s?.module?.[moduleBridgeHex]?.moduleData,
    );
    const permissionsLevel = useSelector(
        (s) => s.module[moduleBridgeHex]?.userBridge?.permissions?.level,
    );
    const isShortVersion = useSelector(
        (s) =>
            s.module[moduleBridgeHex]?.permissions?.resourceManager?.shortVersion,
    );
    const [infoModal, setInfoModal] = useState();
    const [noYesOptions] = useState([
        {
            label: ReactDOMServer.renderToString(
                <Translator file="user" name="general.no" />,
            ),
            value: 0,
        },
        {
            label: ReactDOMServer.renderToString(
                <Translator file="user" name="general.yes" />,
            ),
            value: 1,
        },
    ]);

    const isFilled = (v) => {
        if (Array.isArray(v)) return v.length > 0;
        return v !== null && v !== "" && v !== undefined;
    };

    const validationSchema = Yup.object()
        .shape({
            title: Yup.mixed(),
            fileName: Yup.mixed(),
            hasFile: Yup.mixed().nullable(),
            description: Yup.mixed(),
            dateStampFrom: Yup.mixed(),
            dateStampTo: Yup.mixed(),
            userBridges: Yup.mixed(),
            publicationLocations: Yup.mixed(),
            publicationYear: Yup.mixed(),
            publicationTypes: Yup.mixed(),
            fileTypes: Yup.mixed(),
            mainSubjects: Yup.mixed(),
            secondarySubjects: Yup.mixed(),
            locations: Yup.mixed(),
            naturalRegions: Yup.mixed(),
            tags: Yup.mixed(),
        })
        .test("minFilled", "Debes rellenar al menos 2 campos", function _(values) {
            const filledQuantity = Object.values(values).filter(isFilled);

            if (filledQuantity.length < 2) {
                return this.createError({
                    path: "minFilled",
                    message: "Debes usar al menos 2 filtros para encontrar resultados...",
                });
            }
        });

    const goToRoot = useCallback(
        (hexCode) =>
            navigate(
                path({
                    name: "user_module_documentManager_item_list",
                    params: {
                        moduleBridgeHex,
                        parentItemHex,
                        page: 1,
                        filtersHex: hexCode,
                    },
                }),
            ),
        [moduleBridgeHex, parentItemHex],
    );

    // > GENERATE FILTER HEX CODE
    const generateFilterHexCode = async (values, setSubmitting) =>
        axios
            .post(
                axiosPath(
                    "api_r_user_module_documentManager_item_generateFilterHexCode",
                ),
                { moduleBridgeHex, filters: values },
            )
            .then(({ data }) => {
                setSubmitting(false);
                goToRoot(data.hexCode);
                dispatch(currentPageAction.change("auxSidebar", { opened: null }));
            });

    const handleOnSubmit = (values, { setSubmitting }) => {
        generateFilterHexCode(values, setSubmitting);
    };
    // < GENERATE FILTER HEX CODE

    const removeFilters = useCallback(
        (resetForm) => {
            resetForm();

            navigate(
                path({
                    name: "user_module_documentManager_item_list",
                    params: {
                        moduleBridgeHex,
                        parentItemHex,
                        page: 1,
                        filtersHex: "0",
                    },
                }),
            );
        },
        [parentItemHex],
    );

    // > FILTERS
    const getFilters = useCallback(
        async () =>
            axios
                .post(axiosPath("api_r_user_module_documentManager_item_getFilters"), {
                    moduleBridgeHex,
                    filtersHex,
                })
                .then(({ data }) => {
                    if (isMounted.current) {
                        dispatch(currentPageAction.change("filters", data));
                    }
                }),
        [moduleBridgeHex, filtersHex],
    );

    useEffect(() => {
        if (filtersHex !== "0") {
            getFilters();
        } else {
            dispatch(
                currentPageAction.change(
                    "filters",
                    getDocumentManagerFilterInitialValues(),
                    true,
                ),
            );
        }
    }, [filtersHex]);
    // < FILTERS

    return (
        <>
            {filters && (
                <Formik
                    validateOnChange={false}
                    validateOnBlur={false}
                    validationSchema={permissionsLevel === 1 && validationSchema}
                    initialValues={filters}
                    enableReinitialize
                    onSubmit={handleOnSubmit}
                >
                    {({
                        setFieldValue,
                        handleSubmit,
                        values,
                        errors,
                        isSubmitting,
                        resetForm,
                    }) => (
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                    <FilterWrapper>
                                        <AppInput
                                            title="Título"
                                            value={values.title}
                                            error={errors.title}
                                            onChange={(opt) => setFieldValue("title", opt)}
                                            isClearable
                                        />
                                        <AppRemixIcon
                                            className="ml-2 pointer"
                                            icon="information"
                                            color={colors.primaryLight}
                                            size={20}
                                            onClick={() =>
                                                setInfoModal(
                                                    "Ingresa una palabra o frase contenida en el título de la publicación que deseas consultar.",
                                                )
                                            }
                                        />
                                    </FilterWrapper>
                                </div>
                                {!isShortVersion && (
                                    <>
                                        <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                            <FilterWrapper>
                                                <AppInput
                                                    title="Nombre de archivo"
                                                    value={values.fileName}
                                                    error={errors.fileName}
                                                    onChange={(opt) => setFieldValue("fileName", opt)}
                                                    isClearable
                                                />
                                                <AppRemixIcon
                                                    className="ml-2 pointer"
                                                    icon="information"
                                                    color={colors.primaryLight}
                                                    size={20}
                                                    onClick={() =>
                                                        setInfoModal(
                                                            "Escribe el nombre exacto o parcial del archivo digital (por ejemplo, “impacto_ambiental_2022.pdf”).",
                                                        )
                                                    }
                                                />
                                            </FilterWrapper>
                                        </div>
                                        <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                            <FilterWrapper>
                                                <AppSingleSelect
                                                    title="Archivo vinculado"
                                                    value={values?.hasFile}
                                                    options={noYesOptions}
                                                    onChange={(v) => setFieldValue("hasFile", v)}
                                                    isClearable
                                                />
                                                <AppRemixIcon
                                                    className="ml-2 pointer"
                                                    icon="information"
                                                    color={colors.primaryLight}
                                                    size={20}
                                                    onClick={() =>
                                                        setInfoModal(
                                                            "Selecciona si el documento está asociado a otro recurso complementario en el repositorio.",
                                                        )
                                                    }
                                                />
                                            </FilterWrapper>
                                        </div>
                                        <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                            <FilterWrapper>
                                                <AppMultiSelect
                                                    title="Tipo de archivo"
                                                    options={moduleData?.inputs?.fileTypes}
                                                    value={values?.fileTypes}
                                                    onChange={(opt) => setFieldValue("fileTypes", opt)}
                                                    isClearable
                                                    hasSearchBox
                                                    hasSelectOptions
                                                />
                                                <AppRemixIcon
                                                    className="ml-2 pointer"
                                                    icon="information"
                                                    color={colors.primaryLight}
                                                    size={20}
                                                    onClick={() =>
                                                        setInfoModal(
                                                            "Elige el formato del documento (PDF, Excel, KMZ, imagen, etc.).",
                                                        )
                                                    }
                                                />
                                            </FilterWrapper>
                                        </div>
                                        <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                            <FilterWrapper>
                                                <AppInput
                                                    title="Descripción"
                                                    value={values.description}
                                                    error={errors.description}
                                                    onChange={(opt) => setFieldValue("description", opt)}
                                                    isClearable
                                                />
                                                <AppRemixIcon
                                                    className="ml-2 pointer"
                                                    icon="information"
                                                    color={colors.primaryLight}
                                                    size={20}
                                                    onClick={() =>
                                                        setInfoModal(
                                                            "Palabras clave o resumen breve que esté contenido en la descripción del documento.",
                                                        )
                                                    }
                                                />
                                            </FilterWrapper>
                                        </div>
                                        <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                            <FilterWrapper>
                                                <AppDatePicker
                                                    title="Fecha de aportación desde"
                                                    value={values.dateStampFrom}
                                                    placeholder={ReactDOMServer.renderToString(
                                                        <Translator file="filter" name="filter.select" />,
                                                    )}
                                                    onChange={(opt) =>
                                                        setFieldValue("dateStampFrom", opt)
                                                    }
                                                    isClearable
                                                />
                                                <AppRemixIcon
                                                    className="ml-2 pointer"
                                                    icon="information"
                                                    color={colors.primaryLight}
                                                    size={20}
                                                    onClick={() =>
                                                        setInfoModal(
                                                            "Filtra documentos según el periodo en que fueron cargados al repositorio.",
                                                        )
                                                    }
                                                />
                                            </FilterWrapper>
                                        </div>
                                        <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                            <FilterWrapper>
                                                <AppDatePicker
                                                    title="Fecha de aportación hasta"
                                                    value={values.dateStampTo}
                                                    placeholder={ReactDOMServer.renderToString(
                                                        <Translator file="filter" name="filter.select" />,
                                                    )}
                                                    onChange={(opt) => setFieldValue("dateStampTo", opt)}
                                                    isClearable
                                                />
                                                <AppRemixIcon
                                                    className="ml-2 pointer"
                                                    icon="information"
                                                    color={colors.primaryLight}
                                                    size={20}
                                                    onClick={() =>
                                                        setInfoModal(
                                                            "Filtra documentos según el periodo en que fueron cargados al repositorio.",
                                                        )
                                                    }
                                                />
                                            </FilterWrapper>
                                        </div>
                                    </>
                                )}
                                <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                    <FilterWrapper>
                                        <AppDatePicker
                                            title="Año de publicación"
                                            value={values.publicationYear}
                                            placeholder={ReactDOMServer.renderToString(
                                                <Translator file="filter" name="filter.select" />,
                                            )}
                                            onChange={(opt) => setFieldValue("publicationYear", opt)}
                                            dateFormat="yyyy"
                                            displayDateFormat="YYYY"
                                            showYearPicker
                                            isClearable
                                        />
                                        <AppRemixIcon
                                            className="ml-2 pointer"
                                            icon="information"
                                            color={colors.primaryLight}
                                            size={20}
                                            onClick={() =>
                                                setInfoModal(
                                                    "Año en que el documento fue creado, publicado o actualizado oficialmente.",
                                                )
                                            }
                                        />
                                    </FilterWrapper>
                                </div>
                                {!isShortVersion && (
                                    <>
                                        {permissionsLevel === 0 && (
                                            <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                                <FilterWrapper>
                                                    <AppMultiSelect
                                                        title="Aportado por"
                                                        options={moduleData?.inputs?.userBridges}
                                                        value={values?.userBridges}
                                                        onChange={(opt) =>
                                                            setFieldValue("userBridges", opt)
                                                        }
                                                        isClearable
                                                        hasSearchBox
                                                        hasSelectOptions
                                                    />
                                                    <AppRemixIcon
                                                        className="ml-2 pointer"
                                                        icon="information"
                                                        color={colors.primaryLight}
                                                        size={20}
                                                        onClick={() =>
                                                            setInfoModal(
                                                                "Selecciona el nombre de la persona o institución que compartió el documento con el repositorio.",
                                                            )
                                                        }
                                                    />
                                                </FilterWrapper>
                                            </div>
                                        )}
                                        <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                            <FilterWrapper>
                                                <AppMultiSelect
                                                    title="Dónde fue publicado"
                                                    options={moduleData?.inputs?.publicationLocations}
                                                    value={values?.publicationLocations}
                                                    onChange={(opt) =>
                                                        setFieldValue("publicationLocations", opt)
                                                    }
                                                    isClearable
                                                    hasSearchBox
                                                    hasSelectOptions
                                                />
                                                <AppRemixIcon
                                                    className="ml-2 pointer"
                                                    icon="information"
                                                    color={colors.primaryLight}
                                                    size={20}
                                                    onClick={() =>
                                                        setInfoModal(
                                                            "Indica si el documento fue publicado en el Diario Oficial de la Federación, un periódico oficial, o no aplica.",
                                                        )
                                                    }
                                                />
                                            </FilterWrapper>
                                        </div>
                                        <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                            <FilterWrapper>
                                                <AppMultiSelect
                                                    title="Tipo de publicación"
                                                    options={moduleData?.inputs?.publicationTypes}
                                                    value={values?.publicationTypes}
                                                    onChange={(opt) =>
                                                        setFieldValue("publicationTypes", opt)
                                                    }
                                                    isClearable
                                                    hasSearchBox
                                                    hasSelectOptions
                                                />
                                                <AppRemixIcon
                                                    className="ml-2 pointer"
                                                    icon="information"
                                                    color={colors.primaryLight}
                                                    size={20}
                                                    onClick={() =>
                                                        setInfoModal(
                                                            "Selecciona la naturaleza del documento: acuerdo, informe, resolución, dictamen, etc.",
                                                        )
                                                    }
                                                />
                                            </FilterWrapper>
                                        </div>
                                        <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                            <FilterWrapper>
                                                <AppMultiSelect
                                                    title="Temática principal"
                                                    options={moduleData?.inputs?.mainSubjects}
                                                    value={values?.mainSubjects}
                                                    onChange={(opt) => setFieldValue("mainSubjects", opt)}
                                                    isClearable
                                                    hasSearchBox
                                                    hasSelectOptions
                                                />
                                                <AppRemixIcon
                                                    className="ml-2 pointer"
                                                    icon="information"
                                                    color={colors.primaryLight}
                                                    size={20}
                                                    onClick={() =>
                                                        setInfoModal(
                                                            "Escoge la categoría temática principal que representa el contenido del documento.",
                                                        )
                                                    }
                                                />
                                            </FilterWrapper>
                                        </div>
                                        <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                            <FilterWrapper>
                                                <AppMultiSelect
                                                    title="Temáticas secundarias"
                                                    options={moduleData?.inputs?.secondarySubjects}
                                                    value={values?.secondarySubjects}
                                                    onChange={(opt) =>
                                                        setFieldValue("secondarySubjects", opt)
                                                    }
                                                    isClearable
                                                    hasSearchBox
                                                    hasSelectOptions
                                                />
                                                <AppRemixIcon
                                                    className="ml-2 pointer"
                                                    icon="information"
                                                    color={colors.primaryLight}
                                                    size={20}
                                                    onClick={() =>
                                                        setInfoModal(
                                                            "Puedes seleccionar una subtemática relacionada con el documento.",
                                                        )
                                                    }
                                                />
                                            </FilterWrapper>
                                        </div>
                                        <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                            <FilterWrapper>
                                                <AppMultiSelect
                                                    title="Región natural"
                                                    options={moduleData?.inputs?.naturalRegions}
                                                    value={values?.naturalRegions}
                                                    onChange={(opt) =>
                                                        setFieldValue("naturalRegions", opt)
                                                    }
                                                    isClearable
                                                    hasSearchBox
                                                    hasSelectOptions
                                                />
                                                <AppRemixIcon
                                                    className="ml-2 pointer"
                                                    icon="information"
                                                    color={colors.primaryLight}
                                                    size={20}
                                                    onClick={() =>
                                                        setInfoModal(
                                                            "Selecciona la región ecológica o zona natural referida en el documento (por ejemplo, selva húmeda, desierto).",
                                                        )
                                                    }
                                                />
                                            </FilterWrapper>
                                        </div>
                                        <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                            <FilterWrapper>
                                                <LocationSelect
                                                    value={values.locations}
                                                    onChange={(opt) => setFieldValue("locations", opt)}
                                                    parentLocationID={
                                                        moduleData?.parentLocation?.value ?? 1
                                                    }
                                                />
                                                <AppRemixIcon
                                                    className="ml-2 pointer"
                                                    icon="information"
                                                    color={colors.primaryLight}
                                                    size={20}
                                                    onClick={() =>
                                                        setInfoModal(
                                                            "Indica la entidad federativa o municipio relacionado con el documento.",
                                                        )
                                                    }
                                                />
                                            </FilterWrapper>
                                        </div>
                                        {permissionsLevel === 0 && (
                                            <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                                <FilterWrapper>
                                                    <AppSingleSelect
                                                        title="Archivados"
                                                        value={values?.archived}
                                                        options={noYesOptions}
                                                        onChange={(v) => setFieldValue("archived", v)}
                                                        isClearable
                                                    />
                                                    <AppRemixIcon
                                                        className="ml-2 pointer"
                                                        icon="information"
                                                        color={colors.primaryLight}
                                                        size={20}
                                                        onClick={() =>
                                                            setInfoModal(
                                                                "Filtra según si el documento ya ha sido clasificado como “archivado” o no.",
                                                            )
                                                        }
                                                    />
                                                </FilterWrapper>
                                            </div>
                                        )}
                                    </>
                                )}
                                <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                    <AppMultiSelect
                                        title="Palabras clave"
                                        options={moduleData?.inputs?.tags}
                                        value={values?.tags}
                                        onChange={(opt) => setFieldValue("tags", opt)}
                                        isClearable
                                        hasSearchBox
                                        hasSelectOptions
                                    />
                                </div>
                                <div
                                    className={
                                        isShortVersion
                                            ? "col-12 col-sm-12 col-lg-6 col-xl-3 offset-xl-12"
                                            : permissionsLevel === 0
                                                ? "col-12 col-sm-12 col-lg-6 col-xl-3 offset-xl-3"
                                                : "col-12 col-lg-6 col-xl-3 offset-lg-6 offset-xl-9"
                                    }
                                >
                                    <div className="d-flex mt-2">
                                        {filtersHex !== "0" && (
                                            <button
                                                type="button"
                                                className="btn btn-danger mr-2"
                                                onClick={() => removeFilters(resetForm)}
                                            >
                                                <AppRemixIcon icon="delete-bin-6" />
                                            </button>
                                        )}
                                        <button
                                            type="submit"
                                            className={`btn btn${filtersHex !== "0" ? "-outline" : ""
                                                }-primary flex-grow-1`}
                                            disabled={isSubmitting || _.isEqual(filters, values)}
                                        >
                                            <Translator
                                                file="module-legalCompliance"
                                                name="filtering"
                                            />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            {errors?.minFilled && (
                                <div className="alert alert-warning mt-3">
                                    {errors?.minFilled}
                                </div>
                            )}
                        </form>
                    )}
                </Formik>
            )}
            <AppModal
                show={infoModal}
                title="Información del filtro"
                onClose={() => setInfoModal(undefined)}
            >
                {infoModal}
            </AppModal>
        </>
    );
}

const FilterWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  & > div {
    flex-grow: 1;
  }
  i {
  }
`;
